/**
 * A garbage batch as a simulation is told about it.
 *
 * Named by two absolute ticks, counted from the first tick of the round, and
 * never by a delay or an arrival time: every simulation of a board has to put
 * the batch into the queue on the same tick, or their hashes part company at
 * the next comparison. The server chooses the ticks; everyone applies them.
 */
export interface GarbageSchedule {
  readonly lines: number;
  /** Tick at which the batch enters the queue. Every simulation inserts it here, exactly. */
  readonly queueAtTick: number;
  /** Tick at which it lands, if it has not been cancelled by then. */
  readonly readyAtTick: number;
}

function isCount(value: unknown): value is number {
  return typeof value === 'number' && Number.isSafeInteger(value) && value >= 0;
}

/**
 * Whether a value is a well-formed batch: three whole, non-negative numbers.
 *
 * `SimDriver.scheduleGarbage` refuses anything else, for the same reason
 * `isCurseWindow` exists: these numbers decide when and how hashed state
 * changes.
 */
export function isGarbageSchedule(value: unknown): value is GarbageSchedule {
  if (typeof value !== 'object' || value === null) return false;
  const batch = value as Partial<GarbageSchedule>;
  return isCount(batch.lines) && isCount(batch.queueAtTick) && isCount(batch.readyAtTick);
}
